import { Fragment } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Search } from 'lucide-react'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { NotificationBell } from '@/components/NotificationBell'
import { NAV } from './SidebarNav'

/** Prefer the nav's own wording over a title-cased URL segment. */
function labelFor(path: string, segment: string): string {
  const nav = NAV.find((item) => item.to === path)
  if (nav) return nav.label
  return segment.replace(/-/g, ' ').replace(/^./, (c) => c.toUpperCase())
}

interface Crumb {
  path: string
  label: string
}

export function buildCrumbs(pathname: string): Crumb[] {
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length === 0) return [{ path: '/', label: 'Home' }]
  const crumbs: Crumb[] = [{ path: '/', label: 'Home' }]
  let acc = ''
  for (const segment of segments) {
    acc += `/${segment}`
    crumbs.push({ path: acc, label: labelFor(acc, segment) })
  }
  return crumbs
}

export function AppHeader({ onOpenPalette }: { onOpenPalette: () => void }) {
  const { pathname } = useLocation()
  const crumbs = buildCrumbs(pathname)

  return (
    // Pinned, non-scrolling: the scroll container is the sibling below it in
    // AppShell, so nothing here can move with page content.
    <header className="flex h-14 shrink-0 items-center gap-2 border-b bg-background px-3 md:px-4">
      <SidebarTrigger className="-ml-1" />
      <Separator orientation="vertical" className="mr-1 h-4" />

      <Breadcrumb>
        <BreadcrumbList>
          {crumbs.map((crumb, i) => {
            const last = i === crumbs.length - 1
            return (
              <Fragment key={crumb.path}>
                <BreadcrumbItem>
                  {last ? (
                    <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink asChild>
                      <Link to={crumb.path}>{crumb.label}</Link>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
                {!last && <BreadcrumbSeparator />}
              </Fragment>
            )
          })}
        </BreadcrumbList>
      </Breadcrumb>

      <div className="ml-auto flex items-center gap-1.5">
        <Button
          variant="outline"
          size="sm"
          onClick={onOpenPalette}
          className="gap-2 text-muted-foreground"
          aria-label="Open command palette"
        >
          <Search className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="hidden sm:inline">Search</span>
          <kbd className="hidden rounded border bg-muted px-1 font-mono text-[10px] sm:inline">⌘K</kbd>
        </Button>
        <NotificationBell />
      </div>
    </header>
  )
}
